import React from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, LayoutList, Rows3, Rows4, Maximize2, Minimize2 } from 'lucide-react';

/**
 * ViewToggle - Toggle between different view modes
 * Features:
 * - Compact/Normal view toggle
 * - Table/Card/Grid view toggle
 * - Persists preference to localStorage
 */

// View mode options. No `label` here on purpose (piège #1 des conventions i18n) : le libellé
// est résolu au rendu via t(`common.table.viewModes.${key}`) dans ViewModeToggle ci-dessous.
export const VIEW_MODES = {
  table: { key: 'table', icon: LayoutList },
  list: { key: 'list', icon: LayoutList },
  cards: { key: 'cards', icon: LayoutGrid },
  grouped: { key: 'grouped', icon: LayoutGrid },
  grid: { key: 'grid', icon: Rows4 },
  compact: { key: 'compact', icon: Rows3 },
  // Pipeline-specific modes
  accordion: { key: 'accordion', icon: LayoutGrid },
  timeline: { key: 'timeline', icon: LayoutList }
};

// Density options. Same caveat as VIEW_MODES: no `label`, resolved via t() in DensityToggle.
export const DENSITY_MODES = {
  comfortable: { key: 'comfortable', icon: Maximize2 },
  compact: { key: 'compact', icon: Minimize2 }
};

/**
 * ViewModeToggle - Toggle between table/cards/grid views
 */
export function ViewModeToggle({
  value = 'table',
  onChange,
  options = ['table', 'cards'],
  storageKey = null, // If provided, persists to localStorage
  className = ''
}) {
  const { t } = useTranslation();
  const handleChange = (newValue) => {
    onChange(newValue);
    if (storageKey) {
      localStorage.setItem(storageKey, newValue);
    }
  };

  // Load from localStorage on mount
  React.useEffect(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved && options.includes(saved) && saved !== value) {
        onChange(saved);
      }
    }
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`inline-flex rounded-lg border border-gray-200 bg-white p-1 ${className}`}>
      {options.map(optionKey => {
        const option = VIEW_MODES[optionKey];
        if (!option) return null;
        const IconComponent = option.icon;
        const isActive = value === optionKey;
        const optionLabel = t(`common.table.viewModes.${optionKey}`, { defaultValue: optionKey });

        return (
          <button
            key={optionKey}
            onClick={() => handleChange(optionKey)}
            className={`p-2 rounded-md transition-colors ${
              isActive
                ? 'bg-amber-100 text-amber-700'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
            title={optionLabel}
          >
            <IconComponent className="w-4 h-4" />
          </button>
        );
      })}
    </div>
  );
}

/**
 * DensityToggle - Toggle between comfortable and compact density
 */
export function DensityToggle({
  value = 'comfortable',
  onChange,
  storageKey = null,
  className = ''
}) {
  const { t } = useTranslation();
  const handleChange = (newValue) => {
    onChange(newValue);
    if (storageKey) {
      localStorage.setItem(storageKey, newValue);
    }
  };

  // Load from localStorage on mount
  React.useEffect(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved && ['comfortable', 'compact'].includes(saved) && saved !== value) {
        onChange(saved);
      }
    }
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCompact = value === 'compact';

  return (
    <button
      onClick={() => handleChange(isCompact ? 'comfortable' : 'compact')}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
        isCompact
          ? 'bg-amber-50 border-amber-200 text-amber-700'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
      } ${className}`}
      title={isCompact ? t('common.table.density.switchToNormal') : t('common.table.density.switchToCompact')}
    >
      {isCompact ? (
        <>
          <Minimize2 className="w-4 h-4" />
          <span className="text-sm">{t('common.table.density.compactLabel')}</span>
        </>
      ) : (
        <>
          <Maximize2 className="w-4 h-4" />
          <span className="text-sm">{t('common.table.density.normalLabel')}</span>
        </>
      )}
    </button>
  );
}

/**
 * Combined ViewToggle - Both view mode and density
 */
function ViewToggle({
  viewMode = 'table',
  onViewModeChange,
  viewOptions = ['table', 'cards'],
  density = 'comfortable',
  onDensityChange,
  showDensity = true,
  storageKeyPrefix = null,
  className = ''
}) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <ViewModeToggle
        value={viewMode}
        onChange={onViewModeChange}
        options={viewOptions}
        storageKey={storageKeyPrefix ? `${storageKeyPrefix}_viewMode` : null}
      />
      {showDensity && onDensityChange && (
        <DensityToggle
          value={density}
          onChange={onDensityChange}
          storageKey={storageKeyPrefix ? `${storageKeyPrefix}_density` : null}
        />
      )}
    </div>
  );
}

/**
 * Hook to manage view state with localStorage persistence
 */
export function useViewPreferences(storageKeyPrefix = null, defaults = {}) {
  const [viewMode, setViewMode] = React.useState(defaults.viewMode || 'table');
  const [density, setDensity] = React.useState(defaults.density || 'comfortable');

  // Load from localStorage on mount
  React.useEffect(() => {
    if (storageKeyPrefix) {
      const savedViewMode = localStorage.getItem(`${storageKeyPrefix}_viewMode`);
      const savedDensity = localStorage.getItem(`${storageKeyPrefix}_density`);
      if (savedViewMode) setViewMode(savedViewMode);
      if (savedDensity) setDensity(savedDensity);
    }
  }, [storageKeyPrefix]);

  const updateViewMode = (newMode) => {
    setViewMode(newMode);
    if (storageKeyPrefix) {
      localStorage.setItem(`${storageKeyPrefix}_viewMode`, newMode);
    }
  };

  const updateDensity = (newDensity) => {
    setDensity(newDensity);
    if (storageKeyPrefix) {
      localStorage.setItem(`${storageKeyPrefix}_density`, newDensity);
    }
  };

  // Density-based class names for tables
  const tableClasses = density === 'compact'
    ? { row: 'py-2', cell: 'px-4 py-2 text-sm' }
    : { row: 'py-4', cell: 'px-6 py-4' };

  return {
    viewMode,
    setViewMode: updateViewMode,
    density,
    setDensity: updateDensity,
    isCompact: density === 'compact',
    tableClasses
  };
}

export default ViewToggle;
